import { observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { DownloadButtonListTiny } from "../Buttons/DownloadButtonListTiny";
import { EditButtonListTiny } from "../Buttons/EditButtonListTiny";
import { FavouriteButtonListTiny } from "../Buttons/FavouriteButtonListTiny";
import { SortBy } from "../DocumentReader";
import { FileLink } from "../General/FileLink";
import { AppStore } from "../Stores/AppStore";
import { Strings } from "../Stores/StringsStore";
import {
	DocumentNameElement,
	DocumentsViewEntry,
	handleLoadMore,
	IDocumentsView,
	IDocumentsViewEntryPropsBase,
} from "./DocumentsView";

interface IDocumentList {
	store?: IDocumentsView;
	showValidities: boolean;
	//used for SelectDocumentDialog
	isSelected?: (id: string) => boolean;
	onDocumentClick?: (id: string) => void;
	currentDocument?: string;
}

@inject("store")
@observer
export class DocumentList extends React.Component<IDocumentList, {}> {
	handleLoadMore = (o: any) => {
		handleLoadMore(o, this.props.store);
	};

	render() {
		return (
			<div
				className="area-grid__body-content"
				onScroll={this.handleLoadMore}
			>
				<div className="list-advanced">
					{this.props.store.searchResult.documents.length > 0 ? (
						<table
							data-test-id="documentListTable"
							className="sortable-list is--small-icons"
						>
							<DocumentListHeaders
								isClickable={
									this.props.onDocumentClick ? true : false
								}
							/>
							<DocumentListBody
								showValidities={this.props.showValidities}
								onDocumentClick={this.props.onDocumentClick}
								isSelected={this.props.isSelected}
								currentDocument={this.props.currentDocument}
							/>
						</table>
					) : !this.props.store.isBusy ? (
						<div className="inside__note">
							{this.props.store.noResultMsg}
						</div>
					) : null}

					{this.props.store.isBusy ? (
						<div className="spinner">
							<div className="spinner-inner">
								<i>
									<svg
										className="spinner-animation"
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 48 48"
									>
										<path
											className="spinner-custom"
											d="M24,5A19,19,0,0,1,43,24h3A22,22,0,0,0,24,2Z"
										/>
										<path d="M24,43A19,19,0,0,1,24,5V2A22,22,0,1,0,46,24H43A19,19,0,0,1,24,43Z" />
									</svg>
								</i>
							</div>
							<div className="spinner-label">Loading...</div>
						</div>
					) : null}
				</div>
			</div>
		);
	}
}

interface IDocumentListHeaders {
	isClickable: boolean;
	store?: IDocumentsView;
}

@inject("store")
@observer
class DocumentListHeaders extends React.Component<IDocumentListHeaders, {}> {
	sortBy = (header: string) => {
		let sortBy = new SortBy();
		sortBy.name = header;
		let existing = this.props.store.sortBy.filter((s) => s.name === header);
		if (existing.length > 0) {
			if (existing.some((s) => !s.reverse)) {
				sortBy.reverse = true;
				this.props.store.sortBy = [sortBy];
				this.props.store.skip = 0;
				this.props.store.triggerSearch();
			} else {
				this.props.store.sortBy = [];
				this.props.store.skip = 0;
				this.props.store.triggerSearch();
			}
		} else {
			this.props.store.sortBy = [sortBy];
			this.props.store.skip = 0;
			this.props.store.triggerSearch();
		}
	};

	getHeaderImage(header: string) {
		let existing = this.props.store.sortBy.filter((s) => s.name === header);
		if (existing.length > 0) {
			return existing.some((s) => s.reverse) ? (
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
					<polygon points="32,14 16,24 0,14 0,8 16,18 32,8 " />
				</svg>
			) : (
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
					<polygon points="0,18 16,8 32,18 32,24 16,14 0,24 " />
				</svg>
			);
		}
		return null;
	}

	render() {
		return (
			<thead>
				<tr>
					{AppStore.context.sessionDetails.isApp ? (
						<th className="short-cell" />
					) : null}
					{!this.props.isClickable ? (
						<th className="short-cell fav-cell" />
					) : null}
					{AppStore.context.sessionDetails.isDocumentManager &&
					!this.props.isClickable ? (
						<th className="short-cell" />
					) : null}
					<th className="short-cell" />
					{this.props.store.searchResult.headers.map((header) => {
						let img = this.getHeaderImage(header);
						return (
							<th
								key={header}
								className="sort-header with-caption"
								onClick={() => this.sortBy(header)}
							>
								<span
									className={img ? "is--image" : ""}
									data-test-name="documentListHeaderCell"
								>
									<span>{header}</span>
									{img}
								</span>
							</th>
						);
					})}
					<th className="short-cell with-caption">
						<span data-test-name="documentListHeaderCell">
							{Strings.reader.validFromHeader}
						</span>
					</th>
					<th className="short-cell with-caption">
						<span data-test-name="documentListHeaderCell">
							{Strings.reader.validUntilHeader}
						</span>
					</th>
				</tr>
			</thead>
		);
	}
}

@inject("store")
@observer
export class DocumentListBody extends React.Component<IDocumentList, {}> {
	@observable hoveredDocumentId: string;
	@observable lastId: string;

	onMouseEnter = (documentId: string) => {
		this.hoveredDocumentId = documentId;
	};

	onMouseLeave = () => {
		this.hoveredDocumentId = null;
	};

	onDocumentClick = (id: string) => {
		if (this.props.onDocumentClick) {
			this.props.onDocumentClick(id);
		}
	};

	render() {
		return (
			<tbody>
				{this.props.store.searchResult.documents.map(
					(document, index) => {
						return document.documentId !==
							this.props.currentDocument ? (
							<DocumentListRow
								key={document.fileLinkGuid + index}
								fileResult={document}
								applyUpdatesToModel={false}
								showValidities={this.props.showValidities}
								onDocumentClick={
									this.props.onDocumentClick
										? this.onDocumentClick
										: null
								}
								isSelected={
									this.props.isSelected &&
									this.props.isSelected(document.documentId)
								}
								isHovering={
									this.hoveredDocumentId ===
									document.documentId
								}
								onMouseEnter={this.onMouseEnter}
								onMouseLeave={this.onMouseLeave}
								currentDocument={this.props.currentDocument}
							/>
						) : null;
					}
				)}
			</tbody>
		);
	}
}

interface IDocumentListRow extends IDocumentsViewEntryPropsBase {
	showValidities: boolean;
	onMouseEnter: (documentId: string) => void;
	onMouseLeave: () => void;
	onDocumentClick?: (id: string) => void;
	currentDocument?: string;
}

@inject("store")
@observer
class DocumentListRow extends DocumentsViewEntry<IDocumentListRow> {
	isColumnVisible(index: number): boolean {
		if (this.props.store.showOnlyName) {
			if (!this.props.store.searchResult.headers) return false;
			return (
				this.props.store.searchResult.headers[index] ===
				Strings.documentManagement.documentEdit.readerNameHeader
			);
		} else {
			return true;
		}
	}

	onMouseEnter = () => {
		this.props.onMouseEnter(this.props.fileResult.documentId);
	};

	get className() {
		let result = "";
		if (this.props.onDocumentClick) {
			result += "clickable";
			if (this.props.isSelected) {
				result += " selected";
			}
			if (this.props.isHovering) {
				result += " hover";
			}
		}
		return result;
	}

	onDocumentClick = () => {
		if (this.props.onDocumentClick) {
			this.props.onDocumentClick(this.props.fileResult.documentId);
		}
	};

	preventDefault = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
	};

	render() {
		return (
			<tr
				data-test-name="documentListRow"
				className={this.className}
				onMouseEnter={this.onMouseEnter}
				onMouseLeave={this.props.onMouseLeave}
				onClick={this.onDocumentClick}
				onMouseDown={this.preventDefault}
			>
				{AppStore.context.sessionDetails.isApp ? (
					<td className="short-cell">
						<DownloadButtonListTiny
							onClick={this.handleRequestDownload}
							switchState={this.props.fileResult.status}
						/>
					</td>
				) : null}
				{!this.props.onDocumentClick ? (
					<td className="short-cell fav-cell">
						<FavouriteButtonListTiny
							switchState={this.favouriteState}
							onClick={this.handleAddFavourites}
							testName="favouriteButtonMulti"
						/>
					</td>
				) : null}
				{AppStore.context.sessionDetails.isDocumentManager &&
				!AppStore.context.sessionDetails.isApp &&
				!this.props.onDocumentClick ? (
					<td className="short-cell edit-cell">
						<EditButtonListTiny
							onClick={this.handleEditDocument}
							testName="editButton"
						/>
					</td>
				) : null}
				{this.props.fileResult.region.toLowerCase() == "001" ? (
					<td className="lang-cell">
						<img
							title={this.props.fileResult.languageName}
							src={
								"../../images/flags/" +
								this.props.fileResult.region +
								".png"
							}
						/>
					</td>
				) : (
					<td className="lang-cell">
						<div
							title={this.props.fileResult.languageName}
							className={
								"flag flag-" +
								this.props.fileResult.region.toLowerCase()
							}
						/>
					</td>
				)}

				{this.props.fileResult.values.map((value, index) => {
					return this.isColumnVisible(index) ? (
						<DocumentListRowDynamicCell
							key={
								this.props.fileResult.documentId +
								index.toString()
							}
							fileResult={this.props.fileResult}
							index={index}
							value={value}
							applyUpdatesToModel={false}
							showValidities={this.props.showValidities}
							isClickable={this.props.onDocumentClick !== null}
							isSelected={this.props.isSelected}
							isHovering={this.props.isHovering}
						/>
					) : null;
				})}
				{this.props.showValidities ? (
					<td>
						<span className="tinytool_info">
							{this.props.fileResult.validFrom}
						</span>
					</td>
				) : null}
				{this.props.showValidities ? (
					<td>
						<span className="tinytool_info">
							{this.props.fileResult.validUntil}
						</span>
					</td>
				) : null}
				{AppStore.context.sessionDetails.isApp ? (
					<td>
						<span className="tinytool__info">
							{Math.ceil(this.props.fileResult.size / 1024)} kB
						</span>
					</td>
				) : null}
			</tr>
		);
	}
}

interface IDocumentListRowCellProps extends IDocumentsViewEntryPropsBase {
	showValidities: boolean;
	isClickable: boolean;
	value: string;
	index: number;
}

@inject("store")
@observer
class DocumentListRowDynamicCell extends React.Component<
	IDocumentListRowCellProps,
	{}
> {
	get isLinkCell(): boolean {
		if (!this.props.store.searchResult.headers) return false;
		return (
			this.props.store.searchResult.headers[this.props.index] ===
			Strings.documentManagement.documentEdit.readerNameHeader
		);
	}

	render() {
		return (
			<td data-test-name={this.props.value}>
				{this.isLinkCell ? (
					this.props.isClickable ? (
						<DocumentNameElement
							documentId={this.props.fileResult.documentId}
							language={this.props.fileResult.language}
							guid={this.props.fileResult.fileLinkGuid}
							name={this.props.value}
							mimeType={this.props.fileResult.mimeType}
							testName="fileLink"
						/>
					) : (
						<FileLink
							documentId={this.props.fileResult.documentId}
							language={this.props.fileResult.language}
							guid={this.props.fileResult.fileLinkGuid}
							name={this.props.value}
							mimeType={this.props.fileResult.mimeType}
							testName="fileLink"
							isCoverAvailable={
								this.props.fileResult.isCoverAvailable
							}
							fileLinkGuid={this.props.fileResult.fileLinkGuid}
						/>
					)
				) : (
					this.props.value
				)}
			</td>
		);
	}
}
